/**
 * O cartão dos campos personalizados (história 11.3, FR-79) — a **administração** das definições.
 *
 * O mesmo cartão serve aos dois donos: em Configuração → Clientes ele administra os campos de
 * Empresa (`cadastro`); em Configuração → Negócios, os de Negócio (`negocios`). A forma é uma só
 * nos dois serviços, e por isso o cartão recebe a API por prop (`camposDaEmpresa` ou
 * `camposDoNegocio`), e não duas cópias.
 *
 * **Aqui nascem e mudam as definições — e só elas.** Mostrar e preencher os campos nos
 * formulários do Negócio e na ficha da Empresa é da 11.4 e da 11.7 (spec 11.3, "Never").
 *
 * **O tipo não se edita** (mudar o tipo deixaria órfãos os valores gravados): editar troca nome,
 * ordem e — só no tipo `lista` — as opções. Excluir é **lógico** e em dois passos: o campo some dos
 * formulários, e o valor já gravado continua legível no registro.
 *
 * **O nome de custo ou margem é recusado pelo serviço** (`422`, FR-79/AD-3), e a tela mostra a
 * mensagem dele como veio — ela não a reescreve nem a antecipa. O mesmo vale para o `409` de quem
 * tira da lista uma opção ainda em uso.
 *
 * **As opções da `lista` são itens separados**, cada uma no seu campo, com "Adicionar opção" e
 * "Remover" — e não um texto dividido por vírgula, que quebraria a opção que tem vírgula.
 *
 * **A edição só fecha no sucesso.** Recusada (validação local, `409`, `422`), ela fica aberta com o
 * que foi digitado e a mensagem aparece; "Cancelar" descarta, e reabrir mostra os valores atuais.
 *
 * **Criar com o nome de um campo excluído o reativa** (`200`, mesmo `id`), e a tela diz isso.
 *
 * **O desenho é o do protótipo v2 (28/09/2026)**: título "Campos personalizados de negócio",
 * botão navy pequeno "+ Campo" no cabeçalho (que abre o formulário do campo novo), e uma linha por
 * campo — nome · tipo em cinza · "Editar" e "Excluir" como ações-link. O protótipo escreve
 * "podem ser obrigatórios" na nota; o serviço não tem essa marca, e a nota daqui não a promete.
 */

import { type FormEvent, useState } from 'react'

import {
  type ApiDeCamposPersonalizados,
  type CampoPersonalizado,
  NOMES_DOS_TIPOS_DE_CAMPO,
  type TipoDeCampo,
} from '@/api/campos-personalizados'
import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import './listas-de-dominio.css'

/** O teto do nome (`TAMANHO_DO_NOME_DO_CAMPO` de `compartilhado/campos_personalizados.py`). */
export const TAMANHO_DO_NOME_DO_CAMPO = 80

/** O teto de cada opção (`TAMANHO_DA_OPCAO` do mesmo módulo). */
export const TAMANHO_DA_OPCAO = 80

export const FALTA_NOME_DO_CAMPO = 'Informe o nome do campo.'
export const LISTA_SEM_OPCOES = 'Campo do tipo lista precisa de ao menos uma opção.'
export const CAMPO_CRIADO = 'Campo criado.'
export const CAMPO_REATIVADO = 'O campo existia excluído e voltou para a lista.'
export const CAMPO_SALVO = 'Campo salvo.'
export const CAMPO_EXCLUIDO =
  'Campo excluído. Ele some dos formulários, e os valores já gravados continuam legíveis.'

const TIPOS = Object.keys(NOMES_DOS_TIPOS_DE_CAMPO) as readonly TipoDeCampo[]

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** As opções como o corpo as leva: aparadas, sem as vazias e sem repetir — nunca divididas. */
export function opcoesLimpas(opcoes: readonly string[]): string[] {
  const limpas: string[] = []
  for (const opcao of opcoes) {
    const aparada = opcao.trim()
    if (aparada !== '' && !limpas.includes(aparada)) limpas.push(aparada)
  }
  return limpas
}

/**
 * O que um formulário (de criar ou de editar) confere antes da rede: o nome e, na `lista`, ao
 * menos uma opção. Devolve a mensagem da recusa, ou `null`.
 */
function recusaLocal(nome: string, tipo: TipoDeCampo, opcoes: readonly string[]): string | null {
  if (nome.trim() === '') return FALTA_NOME_DO_CAMPO
  if (tipo === 'lista' && opcoesLimpas(opcoes).length === 0) return LISTA_SEM_OPCOES
  return null
}

/** As opções de uma `lista`, uma por campo, com "Adicionar opção" e "Remover opção N". */
function EditorDeOpcoes({
  idBase,
  opcoes,
  aoTrocar,
}: {
  idBase: string
  opcoes: readonly string[]
  aoTrocar: (opcoes: string[]) => void
}) {
  return (
    <fieldset className="listas__campo">
      <legend className="listas__rotulo">Opções</legend>
      {opcoes.map((opcao, indice) => {
        const numero = String(indice + 1)
        return (
          // A posição é a identidade da linha enquanto se digita: as opções não têm `id`.
          <div key={indice} className="listas__acoes">
            <input
              id={`${idBase}-opcao-${numero}`}
              className="listas__entrada"
              aria-label={`Opção ${numero}`}
              value={opcao}
              maxLength={TAMANHO_DA_OPCAO}
              onChange={(evento) => {
                aoTrocar(opcoes.map((atual, i) => (i === indice ? evento.target.value : atual)))
              }}
            />
            <button
              type="button"
              className="listas__botao listas__botao--discreto"
              aria-label={`Remover opção ${numero}`}
              onClick={() => {
                aoTrocar(opcoes.filter((_, i) => i !== indice))
              }}
            >
              Remover
            </button>
          </div>
        )
      })}
      <div className="listas__acoes">
        <button
          type="button"
          className="listas__botao listas__botao--discreto"
          onClick={() => {
            aoTrocar([...opcoes, ''])
          }}
        >
          Adicionar opção
        </button>
      </div>
    </fieldset>
  )
}

/** O que a linha pede ao cartão ao salvar: `aoConcluir` fecha a edição, e só no sucesso. */
interface PedidoDeEdicao {
  nome: string
  ordem: number
  opcoes: string[]
  aoConcluir: () => void
}

function LinhaDoCampo({
  campo,
  ocupado,
  aoSalvar,
  aoRecusar,
  aoExcluir,
}: {
  campo: CampoPersonalizado
  ocupado: boolean
  aoSalvar: (campo: CampoPersonalizado, pedido: PedidoDeEdicao) => void
  aoRecusar: (mensagem: string) => void
  aoExcluir: (campo: CampoPersonalizado) => void
}) {
  const [editando, setEditando] = useState(false)
  const [confirmando, setConfirmando] = useState(false)
  const [nome, setNome] = useState(campo.nome)
  const [ordem, setOrdem] = useState(String(campo.ordem))
  const [opcoes, setOpcoes] = useState<string[]>([...campo.opcoes])

  /** Abrir a edição **sempre** a partir dos valores atuais — o que ficou de um Cancelar some. */
  function abrirEdicao() {
    setNome(campo.nome)
    setOrdem(String(campo.ordem))
    setOpcoes([...campo.opcoes])
    setEditando(true)
  }

  if (editando) {
    return (
      <li className="campos__linha campos__linha--edicao">
        <form
          className="listas__formulario"
          onSubmit={(evento) => {
            evento.preventDefault()
            const recusa = recusaLocal(nome, campo.tipo, opcoes)
            if (recusa !== null) {
              aoRecusar(recusa)
              return
            }
            aoSalvar(campo, {
              nome: nome.trim(),
              ordem: Number(ordem) || 0,
              opcoes: campo.tipo === 'lista' ? opcoesLimpas(opcoes) : [],
              aoConcluir: () => {
                setEditando(false)
              },
            })
          }}
        >
          <div className="listas__campo">
            <label className="listas__rotulo" htmlFor={`campo-nome-${campo.id}`}>
              Nome
            </label>
            <input
              id={`campo-nome-${campo.id}`}
              className="listas__entrada"
              value={nome}
              maxLength={TAMANHO_DO_NOME_DO_CAMPO}
              onChange={(evento) => {
                setNome(evento.target.value)
              }}
            />
          </div>
          <div className="listas__campo">
            <label className="listas__rotulo" htmlFor={`campo-ordem-${campo.id}`}>
              Ordem
            </label>
            <input
              id={`campo-ordem-${campo.id}`}
              className="listas__entrada"
              type="number"
              min={0}
              value={ordem}
              onChange={(evento) => {
                setOrdem(evento.target.value)
              }}
            />
          </div>
          {campo.tipo === 'lista' ? (
            <EditorDeOpcoes idBase={`campo-${campo.id}`} opcoes={opcoes} aoTrocar={setOpcoes} />
          ) : null}
          <div className="listas__acoes">
            <button type="submit" className="listas__botao" disabled={ocupado}>
              Salvar
            </button>
            <button
              type="button"
              className="listas__botao listas__botao--discreto"
              onClick={() => {
                setEditando(false)
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      </li>
    )
  }

  return (
    <li className="campos__linha">
      <span className="campos__nome">{campo.nome}</span>
      <span className="campos__tipo">
        {NOMES_DOS_TIPOS_DE_CAMPO[campo.tipo]}
        {campo.tipo === 'lista' ? ` · ${campo.opcoes.join(', ')}` : ''}
      </span>
      <div className="campos__acoes">
        {confirmando ? (
          <>
            <button
              type="button"
              className="listas__acao listas__acao--perigo"
              disabled={ocupado}
              onClick={() => {
                aoExcluir(campo)
                setConfirmando(false)
              }}
            >
              Confirmar exclusão
            </button>
            <button
              type="button"
              className="listas__acao"
              onClick={() => {
                setConfirmando(false)
              }}
            >
              Cancelar
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="listas__acao"
              aria-label={`Editar ${campo.nome}`}
              onClick={abrirEdicao}
            >
              Editar
            </button>
            <button
              type="button"
              className="listas__acao listas__acao--perigo"
              aria-label={`Excluir ${campo.nome}`}
              onClick={() => {
                setConfirmando(true)
              }}
            >
              Excluir
            </button>
          </>
        )}
      </div>
    </li>
  )
}

export interface PropsDosCamposPersonalizados {
  /** A API do dono: `camposDaEmpresa` (cadastro) ou `camposDoNegocio` (negocios). */
  api: ApiDeCamposPersonalizados
  /** "Negócio" ou "Cliente" — o título do cartão diz de quem são os campos. */
  entidade: 'Negócio' | 'Cliente'
}

export function CamposPersonalizados({ api, entidade }: PropsDosCamposPersonalizados) {
  const consulta = api.useCampos()
  const criacao = api.useCriarCampo()
  const edicao = api.useEditarCampo()
  const exclusao = api.useExcluirCampo()

  /** O formulário do campo novo, atrás do "+ Campo" do cabeçalho (protótipo v2). */
  const [criando, setCriando] = useState(false)
  const [nome, setNome] = useState('')
  const [tipo, setTipo] = useState<TipoDeCampo>('texto')
  const [opcoes, setOpcoes] = useState<string[]>([''])
  const [erro, setErro] = useState<string | null>(null)
  const [confirmacao, setConfirmacao] = useState<string | null>(null)

  const ocupado = criacao.isPending || edicao.isPending || exclusao.isPending
  const idDoTitulo = `campos-personalizados-${api.dono}`

  function limpar() {
    setErro(null)
    setConfirmacao(null)
  }

  function aoFalhar(falha: unknown) {
    setConfirmacao(null)
    setErro(mensagemDe(falha))
  }

  function recusar(mensagem: string) {
    setConfirmacao(null)
    setErro(mensagem)
  }

  function criar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    limpar()
    const recusa = recusaLocal(nome, tipo, opcoes)
    if (recusa !== null) {
      setErro(recusa)
      return
    }
    criacao.mutate(
      { nome: nome.trim(), tipo, opcoes: tipo === 'lista' ? opcoesLimpas(opcoes) : [] },
      {
        onSuccess: ({ restaurado }) => {
          setConfirmacao(restaurado ? CAMPO_REATIVADO : CAMPO_CRIADO)
          setNome('')
          setOpcoes([''])
        },
        onError: aoFalhar,
      },
    )
  }

  const idDoFormulario = `${idDoTitulo}-novo`

  return (
    <section className="listas" aria-labelledby={idDoTitulo}>
      <div className="listas__cartao">
        <div className="listas__cabecalho listas__cabecalho--linha">
          <div className="listas__cabecalho-texto">
            <h2 className="listas__titulo" id={idDoTitulo}>
              Campos personalizados de {entidade.toLowerCase()}
            </h2>
            <span className="listas__ajuda">
              Aparecem na ficha. Texto, número, data ou lista de opções; o tipo não muda depois de
              criado, e campo de custo ou margem não existe no CRM.
            </span>
          </div>
          <button
            type="button"
            className="listas__botao listas__botao--pequeno"
            aria-expanded={criando}
            aria-controls={criando ? idDoFormulario : undefined}
            onClick={() => {
              limpar()
              setCriando((aberto) => !aberto)
            }}
          >
            + Campo
          </button>
        </div>

        {criando ? (
          <div className="listas__corpo" id={idDoFormulario}>
            <form className="listas__formulario" onSubmit={criar}>
              <div className="listas__campo">
                <label className="listas__rotulo" htmlFor={`${idDoTitulo}-nome`}>
                  Novo campo
                </label>
                <input
                  id={`${idDoTitulo}-nome`}
                  className="listas__entrada"
                  value={nome}
                  maxLength={TAMANHO_DO_NOME_DO_CAMPO}
                  onChange={(evento) => {
                    setNome(evento.target.value)
                  }}
                />
              </div>
              <div className="listas__campo">
                <label className="listas__rotulo" htmlFor={`${idDoTitulo}-tipo`}>
                  Tipo
                </label>
                <select
                  id={`${idDoTitulo}-tipo`}
                  className="listas__entrada"
                  value={tipo}
                  onChange={(evento) => {
                    setTipo(evento.target.value as TipoDeCampo)
                  }}
                >
                  {TIPOS.map((umTipo) => (
                    <option key={umTipo} value={umTipo}>
                      {NOMES_DOS_TIPOS_DE_CAMPO[umTipo]}
                    </option>
                  ))}
                </select>
              </div>
              {tipo === 'lista' ? (
                <EditorDeOpcoes idBase={`${idDoTitulo}-novo`} opcoes={opcoes} aoTrocar={setOpcoes} />
              ) : null}
              <div className="listas__acoes">
                <button type="submit" className="listas__botao" disabled={ocupado}>
                  Criar campo
                </button>
                <button
                  type="button"
                  className="listas__botao listas__botao--discreto"
                  aria-label="Cancelar o campo novo"
                  onClick={() => {
                    limpar()
                    setCriando(false)
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        ) : null}

        {consulta.isError ? (
          <div className="listas__corpo">
            <p className="listas__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
          </div>
        ) : consulta.isPending ? (
          <p className="campos__vazio" role="status">
            Carregando os campos personalizados…
          </p>
        ) : consulta.data.length === 0 ? (
          <p className="campos__vazio">Nenhum campo personalizado.</p>
        ) : (
          <ul className="campos__linhas" aria-label={`Campos personalizados de ${entidade}`}>
            {consulta.data.map((campo) => (
              <LinhaDoCampo
                key={campo.id}
                campo={campo}
                ocupado={ocupado}
                aoRecusar={recusar}
                aoSalvar={(atual, pedido) => {
                  limpar()
                  edicao.mutate(
                    {
                      campoId: atual.id,
                      edicao: { nome: pedido.nome, ordem: pedido.ordem, opcoes: pedido.opcoes },
                    },
                    {
                      onSuccess: () => {
                        pedido.aoConcluir()
                        setConfirmacao(CAMPO_SALVO)
                      },
                      onError: aoFalhar,
                    },
                  )
                }}
                aoExcluir={(atual) => {
                  limpar()
                  exclusao.mutate(atual.id, {
                    onSuccess: () => {
                      setConfirmacao(CAMPO_EXCLUIDO)
                    },
                    onError: aoFalhar,
                  })
                }}
              />
            ))}
          </ul>
        )}

        {erro === null && confirmacao === null ? null : (
          <div className="listas__corpo">
            {erro === null ? null : (
              <p className="listas__erro" role="alert">
                {erro}
              </p>
            )}
            {confirmacao === null || erro !== null ? null : (
              <p className="listas__sucesso" role="status">
                {confirmacao}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
